import React, { useState, useEffect, useCallback, useRef } from 'react';
import { MdClose, MdInfo, MdArrowDropDown, MdArrowDropUp } from 'react-icons/md';
import { detectCookieIssues, clearCookieIssueFlags, wasProfileFetchSuccessful } from '../../utils/cookieHelper';
import { useLocation } from 'react-router-dom';

const CookieBanner = () => {
  const [visible, setVisible] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [crossOrigin, setCrossOrigin] = useState(false);
  const [apiUrl, setApiUrl] = useState('');
  const [frontendUrl, setFrontendUrl] = useState('');
  const location = useLocation();
  
  // Track last event timestamp to prevent excessive updates
  const lastEventRef = useRef({
    storage: 0,
    cookieIssue: 0
  });
  
  // Track if the component is mounted
  const isMountedRef = useRef(true);

  const checkCookieIssue = useCallback(() => {
    // Check flags that indicate cookie issues
    const hasCookieIssue = localStorage.getItem('cookie_issue') === 'true';
    const hasCookieAuthIssue = localStorage.getItem('cookie_auth_issue') === 'true';
    const hasRecentlyLoggedIn = !!localStorage.getItem('user');
    
    // Check if cookie issue happened recently (within the last hour)
    let issueFresh = false;
    const issueTimestamp = localStorage.getItem('cookie_issue_timestamp');
    if (issueTimestamp) {
      const now = Date.now();
      const issueTime = parseInt(issueTimestamp);
      const MAX_AGE = 60 * 60 * 1000; // 1 hour
      issueFresh = !isNaN(issueTime) && (now - issueTime < MAX_AGE);
    }
    
    // For logged-in users, check if profile fetch was successful
    if (hasRecentlyLoggedIn) {
      const profileFetchOk = wasProfileFetchSuccessful();
      if (profileFetchOk) {
        // If profile fetch was successful, clear cookie issue flags
        clearCookieIssueFlags();
        return false;
      }
      
      // In incognito mode, if user is logged in and localStorage works,
      // don't show the banner as token-based auth is working
      try {
        const userData = localStorage.getItem('user');
        if (userData) {
          // User is logged in via localStorage - token-based auth is working
          // Only show banner if there's a persistent issue
          const persistentIssue = hasCookieAuthIssue && issueFresh;
          if (!persistentIssue) {
            // Clear flags if token-based auth is working
            clearCookieIssueFlags();
            return false;
          }
        }
      } catch (e) {
        // localStorage might not be available
      }
    }
    
    // Return true only if there's a fresh cookie issue AND user is not successfully using token-based auth
    return (hasCookieIssue || hasCookieAuthIssue) && issueFresh;
  }, []);

  // Memoize the update visibility function
  const updateVisibility = useCallback(() => {
    if (!isMountedRef.current) return;
    
    // Check if cookie_issue flag is set to 'true'
    const shouldShowBanner = checkCookieIssue();
    
    if (shouldShowBanner) {
      // Get crossOrigin information for detailed instructions
      const cookieStatus = detectCookieIssues();
      if (cookieStatus.crossOrigin) {
        setCrossOrigin(true);
        setApiUrl(cookieStatus.apiUrl || '');
        setFrontendUrl(cookieStatus.frontendUrl || window.location.origin);
      }
      
      setVisible(true);
    } else {
      setVisible(false);
    }
  }, [checkCookieIssue]);
  
  // Handler for storage events (debounced)
  const handleStorageChange = useCallback(() => {
    const now = Date.now();
    if (now - lastEventRef.current.storage < 300) {
      return; // Throttle frequent calls
    }
    lastEventRef.current.storage = now;
    
    updateVisibility();
  }, [updateVisibility]);
  
  // Handler for cookie issue events (debounced)
  const handleCookieIssueEvent = useCallback(() => {
    const now = Date.now();
    if (now - lastEventRef.current.cookieIssue < 300) {
      return; // Throttle frequent calls
    }
    lastEventRef.current.cookieIssue = now;
    
    updateVisibility();
  }, [updateVisibility]);

  useEffect(() => {
    // Set the mounted ref
    isMountedRef.current = true;
    
    // Initial check
    updateVisibility();
    
    // Add event listeners
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('cookie-issue-update', handleCookieIssueEvent);
    
    // Clean up function
    return () => {
      isMountedRef.current = false;
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('cookie-issue-update', handleCookieIssueEvent);
    };
  }, [ updateVisibility, handleStorageChange, handleCookieIssueEvent]);

  const dismissBanner = () => {
    // When user dismisses, we don't clear the flags, just hide the banner
    setVisible(false);
  };

  const retryLogin = () => {
    // Clear all cookie-related flags before redirecting
    clearCookieIssueFlags();
    window.location.href = '/';
  };

  if (!visible) return null;

  return (
    <div className="fixed inset-x-0 bottom-0 z-50 bg-red-900/90 text-white p-4 shadow-lg border-t border-red-600">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-3">
            <div className="mt-1 text-red-300 flex-shrink-0">
              <MdInfo size={24} />
            </div>
            <div>
              <h3 className="font-semibold text-lg">Authentication Cookie Issue Detected</h3>
              <p className="mt-1">
                Your browser is blocking authentication cookies which are required for a better experience.
                Please enable third-party cookies in your browser settings to use all features.
              </p>
              
              <button 
                onClick={() => setExpanded(!expanded)}
                className="flex items-center text-red-300 hover:text-white mt-2 text-sm"
              >
                {expanded ? (
                  <>
                    <MdArrowDropUp className="mr-1" /> Hide browser instructions
                  </>
                ) : (
                  <>
                    <MdArrowDropDown className="mr-1" /> Show browser instructions
                  </>
                )}
              </button>
              
              {expanded && (
                <div className="mt-3 bg-black/20 p-3 rounded-lg text-sm">
                  {crossOrigin && (
                    <div className="mb-3 text-red-300 text-xs">
                      <p>Cross-origin context detected:</p>
                      <ul className="list-disc pl-4 mt-1">
                        <li>Frontend: {frontendUrl}</li>
                        <li>API: {apiUrl}</li>
                      </ul>
                    </div>
                  )}
                  
                  <h4 className="font-semibold mb-2">How to enable cookies:</h4>
                  <ul className="space-y-2 list-disc pl-5">
                    <li><strong>Chrome:</strong> Settings → Privacy and Security → Cookies → Allow third party cookies</li>
                    <li><strong>Firefox:</strong> Settings → Privacy & Security → Cookies and Site Data → Cross-site tracking cookies</li>
                    <li><strong>Safari:</strong> Preferences → Privacy → Website tracking → uncheck 'Prevent cross-site tracking'</li>
                    <li><strong>Edge:</strong> Settings → Privacy, search, and services → Cookies → Turn off 'Block third-party cookies'</li>
                    <li><strong>Brave:</strong> Settings → Privacy & Security → Site & Shield Settings → Block third-party cookies → Add pumpgym.in to exceptions</li>
                  </ul>
                </div>
              )}
              
              <div className="mt-4 flex gap-3">
                <button
                  onClick={retryLogin}
                  className="bg-white text-red-900 px-4 py-2 rounded-lg font-medium hover:bg-gray-100"
                >
                  Retry Login
                </button>
                <button
                  onClick={dismissBanner}
                  className="bg-red-800 text-white px-4 py-2 rounded-lg font-medium hover:bg-red-700"
                >
                  Dismiss
                </button>
              </div>
            </div>
          </div>
          <button onClick={dismissBanner} className="text-white hover:text-red-300">
            <MdClose size={24} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default CookieBanner; 